import type { CustomTheme } from "@/theme/theme";
import Button from "@mui/material/Button";
import type { OverrideProps } from "@mui/material/OverridableComponent";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { SxProps, Theme } from "@mui/system";
import { useTheme } from "@mui/system";
import type React from "react";

interface CustomButtonProps {
  label: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  startIcon?: React.ReactElement<SvgIconProps>;
  variant?:
    | "primary"
    | "secondary"
    | "inverse"
    | "danger"
    | "cancel"
    | "noLine"
    | "dashed";
  size?: "mi" | "sm" | "md" | "lg";
  sx?: SxProps<Theme>;
  onKeyPress?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  disableRipple?: boolean;
}

interface ExtendButtonBaseTypeMap {
  props: CustomButtonProps;
  defaultComponent: React.ElementType;
}

type OverrideButtonProps<
  RootComponent extends React.ElementType = typeof Button
> = OverrideProps<ExtendButtonBaseTypeMap, RootComponent> & {
  component?: RootComponent | undefined;
};

type OverridableButtonProps<
  C extends React.ElementType | undefined = undefined
> = {
  component?: C;
} & (C extends React.ElementType ? OverrideButtonProps<C> : CustomButtonProps);

export const CustomButton = <
  C extends React.ElementType | undefined = undefined
>({
  label,
  onClick,
  disabled = false,
  startIcon,
  variant = "primary",
  size = "sm",
  sx,
  onKeyPress,
  component,
  disableRipple,
  ...props
}: OverridableButtonProps<C>) => {
  const theme: CustomTheme = useTheme();

  const defaultStyles = {
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      fontWeight: "bold",
    },
    secondary: {
      backgroundColor: theme.customPalette.background.gray,
      color: "black",
      fontWeight: "bold",
      "&:hover": {
        backgroundColor: theme.customPalette.hover,
      },
    },
    inverse: {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      border: "1px solid",
      borderColor: disabled
        ? theme.palette.background.default
        : theme.palette.primary.main,
    },
    danger: {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      border: "1px solid",
      borderColor: disabled
        ? theme.palette.background.default
        : theme.palette.primary.main,
    },
    cancel: {
      backgroundColor: "#FFFFFF",
      color: theme.palette.primary.main,
      border: "0.1px solid",
      borderColor: disabled ? theme.palette.background.default : "#B4B4B4",
      boxShadow: "0px 1px 1px rgba(0,0,0,0.2)",
      fontWeight: "bold",
      "&:hover": {
        backgroundColor: theme.customPalette.hover,
      },
    },
    noLine: {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      border: "1px solid",
      borderColor: "transparent",
      // disable時のスタイル
      "&:disabled": {
        backgroundColor: "transparent",
        color: theme.palette.background.default,
        border: "1px solid",
        borderColor: "transparent",
      },
      "&:hover": {
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        border: "1px solid",
        fontWeight: "bold",
        borderColor: "transparent",
      },
      "&:focus": {
        outline: "none",
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        border: "1px solid",
        borderColor: "transparent",
      },
    },
    dashed: {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      border: "1px dashed",
      borderColor: disabled
        ? theme.palette.background.default
        : theme.palette.primary.main,
      "&:hover": {
        backgroundColor: "transparent",
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      },
    },
  };

  // Default sizes for the button
  const defaultSizes = {
    mi: {
      paddingY: 1,
      paddingX: 0,
      minHeight: "32px",
      fontSize: theme.typography.body1.fontSize,
      borderRadius: "24px",
    },
    sm: {
      paddingX: 3,
      minWidth: "84px",
      height: "28px",
      fontSize: theme.typography.body1.fontSize,
      borderRadius: "8px",
    },
    md: {
      paddingX: 4,
      minWidth: "118px",
      height: "28px",
      fontSize: theme.typography.body1.fontSize,
      borderRadius: "8px",
    },
    lg: {
      paddingY: 1,
      paddingX: 4,
      minWidth: "210px",
      minHeight: "32px",
      fontSize: theme.typography.body1.fontSize,
      borderRadius: "24px",
    },
  };

  return (
    <Button
      component={component}
      variant="contained"
      disableElevation
      onClick={onClick}
      disabled={disabled}
      startIcon={startIcon}
      sx={{
        ...defaultStyles[variant],
        ...defaultSizes[size],
        ...sx, // allow user to overwrite styles
      }}
      onKeyPress={onKeyPress}
      disableRipple={disableRipple}
      {...props}
    >
      {label}
    </Button>
  );
};
